import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { AssessmentRunOption } from '../../shared/utils';
import IpptInnerForm from '../IpptInnerForm';
import { baseIpptPerformanceFormSchema, type EditIpptPerformanceFormData } from '../IpptPerformanceFormConfig';
import type { IpptPerformanceRowData } from '../IpptPerformanceTab';
import {
    UpdatePerformanceIpptResultsDocument,
    type UpdatePerformanceIpptResultsMutation,
} from './EditPerformanceIpptResults.api';
import client from '@/apolloClient';
import Button from '@/components/common/Button';
import Modal from '@/components/common/Modal';
import DatePickerField from '@/components/fields/DatePickerField';
import SelectField from '@/components/fields/SelectField';
import SwitchField from '@/components/fields/SwitchField';
import { useGetCompanyId } from '@/contexts/UserSession';
import { splitSeconds } from '@/utils/common';
import { success as showSuccessNotification, error as showErrorNotification } from '@/utils/notifications/actions';

interface EditPerformanceIpptModalProps {
    open: boolean;
    onClose: (refresh?: boolean) => void;
    rowData: IpptPerformanceRowData | null;
    assessmentRunOptions: AssessmentRunOption[];
}

const EditPerformanceIpptModal = ({ open, onClose, rowData, assessmentRunOptions }: EditPerformanceIpptModalProps) => {
    const { t } = useTranslation('performanceResult');
    const selectedCompanyId = useGetCompanyId();

    const { minutes: runTimeMinutes, seconds: runTimeSeconds } = splitSeconds(rowData?.runTimeSec);

    const form = useForm<EditIpptPerformanceFormData>({
        mode: 'onChange',
        resolver: zodResolver(baseIpptPerformanceFormSchema),
        defaultValues: {
            pushUps: rowData?.pushUps ?? undefined,
            sitUps: rowData?.sitUps ?? undefined,
            pullUps: rowData?.pullUps ?? undefined,
            runTime: rowData?.runTimeSec ?? undefined,
            points: rowData?.points ?? undefined,
            date: rowData?.assessmentDate ?? undefined,
            runTimeMinutes,
            runTimeSeconds,
            isTiedToAssessmentRun: rowData?.isTiedToAssessmentRun ?? false,
            assessmentRunId: rowData?.assessmentRunId ?? undefined,
        },
    });

    const isTiedToAssessmentRun = form.watch('isTiedToAssessmentRun');

    useEffect(() => {
        if (rowData?.isTiedToAssessmentRun && rowData.assessmentRunId) {
            form.setValue('assessmentRunId', rowData.assessmentRunId);
        }
    }, [form, rowData]);

    const handleSubmit = useCallback(async () => {
        if (!(await form.trigger()) || !rowData) {
            return;
        }
        const values = form.getValues();

        try {
            const { data } = await client.mutate<UpdatePerformanceIpptResultsMutation>({
                mutation: UpdatePerformanceIpptResultsDocument,
                variables: {
                    id: rowData.id,
                    input: {
                        companyId: selectedCompanyId || '',
                        assessmentRunId: values.isTiedToAssessmentRun ? values.assessmentRunId || undefined : undefined,
                        result: {
                            pushUps: values.pushUps ?? undefined,
                            sitUps: values.sitUps ?? undefined,
                            pullUps: values.pullUps ?? undefined,
                            runTimeSec: values.runTime ?? undefined,
                            points: values.points ?? undefined,
                        },
                        completedAt: values.date ?? undefined,
                    },
                },
            });

            if (data?.updateIpptResult?.__typename === 'ErrorResponse') {
                const errorResponse = data.updateIpptResult;
                const specificErrorField = errorResponse.fields?.find(f => f.message);
                const errorMessage = specificErrorField?.message || t('updateError.description', { type: 'IPPT' });

                showErrorNotification({
                    message: t('updateError.title', { type: 'IPPT' }),
                    description: errorMessage,
                });

                throw new Error(errorMessage);
            } else {
                showSuccessNotification({
                    message: t('updateSuccess.title', { type: 'IPPT' }),
                    description: t('updateSuccess.message', { type: 'IPPT' }),
                });

                onClose(true);
            }
        } catch (error) {
            console.error('Error updating IPPT performance result:', error);
            throw error;
        }
    }, [form, onClose, rowData, selectedCompanyId, t]);

    const modalFooter = (
        <div className="mt-5 flex w-full justify-end">
            <div className="flex space-x-2">
                <Button outline onClick={() => onClose()}>
                    {t('ippt.editModal.cancelBtn')}
                </Button>
                <Button color="teal" onClick={handleSubmit}>
                    {t('ippt.editModal.saveBtn')}
                </Button>
            </div>
        </div>
    );

    return (
        <FormProvider {...form}>
            <Modal
                open={open}
                onClose={onClose}
                title={t('ippt.editModal.title')}
                footer={modalFooter}
                showCloseButton={false}
                className="w-full sm:w-xl"
                largeTitle
            >
                <form onSubmit={handleSubmit}>
                    <div className="grid gap-4 sm:grid-cols-3">
                        <div className="sm:col-span-3">
                            <DatePickerField name="date" label={t('ippt.form.assessmentDate')} required />
                        </div>
                        <IpptInnerForm />
                        <div className="sm:col-span-3">
                            <SwitchField
                                name="isTiedToAssessmentRun"
                                label={t('soc.form.tieToAssessmentRun', {
                                    defaultValue: 'Tie To Assessment Run',
                                })}
                            />
                        </div>
                        {isTiedToAssessmentRun && (
                            <div className="sm:col-span-3">
                                <SelectField
                                    name="assessmentRunId"
                                    label={t('filter.assessmentRun')}
                                    placeholder={t('filter.assessmentRunPlaceholder')}
                                    options={assessmentRunOptions}
                                    required
                                />
                            </div>
                        )}
                    </div>
                </form>
            </Modal>
        </FormProvider>
    );
};

export default EditPerformanceIpptModal;
